import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const Facturacion = () => {
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFactura, setEditingFactura] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFacturas();
    fetchClientes();
    fetchServicios();
  }, []);

  const fetchFacturas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/facturacion');
      // Asegurar que siempre sea un array
      setFacturas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Error al cargar facturas');
      setFacturas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const fetchServicios = async () => {
    try {
      const response = await api.get('/servicios');
      setServicios(response.data);
    } catch (error) {
      console.error('Error al cargar servicios');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const subtotal = values.subtotal || 0;
      const impuesto = values.impuesto || subtotal * 0.18;
      const total = subtotal + impuesto;

      const data = {
        ...values,
        subtotal,
        impuesto,
        total,
      };

      if (editingFactura) {
        await api.put(`/facturacion/${editingFactura.id}`, data);
        message.success('Factura actualizada exitosamente');
      } else {
        await api.post('/facturacion', data);
        message.success('Factura creada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingFactura(null);
      fetchFacturas();
    } catch (error) {
      message.error('Error al guardar factura');
    }
  };

  const handleEdit = (record) => {
    setEditingFactura(record);
    form.setFieldsValue({
      ...record,
      cliente_id: record.cliente_id?.toString(),
      servicio_id: record.servicio_id?.toString(),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/facturacion/${id}`);
      message.success('Factura eliminada exitosamente');
      fetchFacturas();
    } catch (error) {
      message.error('Error al eliminar factura');
    }
  };

  const columns = [
    {
      title: 'Número Factura',
      dataIndex: 'numero_factura',
      key: 'numero_factura',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
    },
    {
      title: 'Servicio',
      dataIndex: 'tipo_servicio',
      key: 'servicio',
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal) => `S/ ${subtotal}`,
    },
    {
      title: 'Impuesto',
      dataIndex: 'impuesto',
      key: 'impuesto',
      render: (impuesto) => `S/ ${impuesto}`,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => <strong>S/ {total}</strong>,
    },
    {
      title: 'Método de Pago',
      dataIndex: 'metodo_pago',
      key: 'metodo_pago',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          pagada: 'green',
          cancelada: 'red',
        };
        return <Tag color={colors[estado]}>{estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Fecha Emisión',
      dataIndex: 'fecha_emision',
      key: 'fecha_emision',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta factura?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Facturación"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingFactura(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nueva Factura
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={facturas}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingFactura ? 'Editar Factura' : 'Nueva Factura'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingFactura(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="cliente_id"
            label="Cliente"
            rules={[{ required: true, message: 'Selecciona un cliente' }]}
          >
            <Select placeholder="Selecciona un cliente">
              {clientes.map((cliente) => (
                <Select.Option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellido}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="servicio_id"
            label="Servicio"
            rules={[{ required: true, message: 'Selecciona un servicio' }]}
          >
            <Select placeholder="Selecciona un servicio">
              {servicios.map((servicio) => (
                <Select.Option key={servicio.id} value={servicio.id}>
                  {servicio.tipo_servicio} - {servicio.descripcion}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="subtotal"
            label="Subtotal"
            rules={[{ required: true, message: 'Ingresa el subtotal' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="impuesto" label="Impuesto (IGV)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="metodo_pago"
            label="Método de Pago"
            rules={[{ required: true, message: 'Selecciona el método de pago' }]}
          >
            <Select placeholder="Selecciona el método de pago">
              <Select.Option value="Efectivo">Efectivo</Select.Option>
              <Select.Option value="Tarjeta">Tarjeta</Select.Option>
              <Select.Option value="Transferencia">Transferencia</Select.Option>
              <Select.Option value="Yape/Plin">Yape/Plin</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="pendiente">
            <Select>
              <Select.Option value="pendiente">Pendiente</Select.Option>
              <Select.Option value="pagada">Pagada</Select.Option>
              <Select.Option value="cancelada">Cancelada</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingFactura ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingFactura(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Facturacion;

